import { createContext, useContext, useEffect, useRef, useState } from "react";

// Instance branding (name, wordmark, icons, regional links) is SERVER-PROVIDED:
// this dashboard is one build shared by every instance (app.adlywise.com,
// app.renomedia.agency, ...), and the backend picks the brand by its TENANT
// (apps/backend/branding/<tenant>/). Fetched on open; the last copy is cached
// so a reload paints the right brand immediately, then revalidated. Afterwards
// only the cheap GET /version is polled, and /branding is refetched when its
// `branding` revision changes.

const API_BASE = import.meta.env?.VITE_API_URL ?? "/api";
const CACHE_KEY = "branding:v1";
const POLL_MS = 5 * 60 * 1000;

// The default brand: Adlywise. Shown until the first response on a browser
// with no cached copy, and kept if /branding can't be reached. Mirrors
// apps/backend/branding/adlywise/brand.json (also the backend's default TENANT).
// `regions` stays empty on purpose: regional links route publisher traffic, so
// they only appear once the server confirms which instance this is.
export const DEFAULT_BRANDING = {
  id: "adlywise",
  name: "Adlywise",
  wordmark: "adlywise",
  productLabel: "DSP",
  tagline: "CTV/OTT Advertising Network",
  documentTitle: "Adlywise Dashboard",
  favicon: null,
  logo: null,
  regions: [],
  revision: "",
};

const STRING_KEYS = [
  "id",
  "name",
  "wordmark",
  "productLabel",
  "tagline",
  "documentTitle",
  "revision",
];
const isNullableString = (v) => v === null || typeof v === "string";

// Shape check for anything that came from storage or the network: a stale or
// corrupted value must never crash rendering (e.g. `regions: {}`).
export const parseBranding = (value) => {
  if (!value || typeof value !== "object") return null;
  if (STRING_KEYS.some((k) => typeof value[k] !== "string")) return null;
  if (!isNullableString(value.favicon) || !isNullableString(value.logo)) {
    return null;
  }
  const regions = value.regions;
  if (
    !Array.isArray(regions) ||
    regions.some(
      (r) => typeof r?.label !== "string" || typeof r?.origin !== "string",
    )
  ) {
    return null;
  }
  return value;
};

const readCache = () => {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    return raw ? parseBranding(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
};

const writeCache = (branding) => {
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(branding));
  } catch {
    // Private mode / storage disabled: the brand still applies, just uncached.
  }
};

export const brandAssetUrl = (branding, name) =>
  name
    ? `${API_BASE}/branding/assets/${encodeURIComponent(name)}?v=${branding.revision}`
    : null;

// GLOBAL (the current host) is always first; the tenant config only lists
// EXTRA regional hosts that serve its own data.
export const brandRegions = (branding) => [
  { label: "GLOBAL", origin: null },
  ...(branding.regions ?? []),
];

export const applyDocumentBranding = (branding, doc = document) => {
  doc.title = branding.documentTitle || "Dashboard";
  const href = brandAssetUrl(branding, branding.favicon);
  let link = doc.querySelector('link[rel="icon"]');
  if (!href) {
    link?.remove();
    return;
  }
  if (!link) {
    link = doc.createElement("link");
    link.rel = "icon";
    doc.head.appendChild(link);
  }
  link.href = href;
};

const fetchJson = async (path) => {
  const res = await fetch(`${API_BASE}${path}`, { cache: "no-store" });
  if (!res.ok) throw new Error(`${path} -> ${res.status}`);
  return res.json();
};

// load(): fetch /branding and publish it. check(): poll the cheap /version and
// load() only when its `branding` revision differs from what is showing.
// Failures and invalid payloads keep the current brand (cached or default);
// the next check retries. Only the most recently STARTED load may publish, so
// an older response landing late can't roll the brand back.
export const createBrandingSync = ({
  fetchJson: get = fetchJson,
  initialRevision = "",
  onBranding,
}) => {
  let revision = initialRevision;
  let stopped = false;
  let generation = 0;
  const load = async () => {
    const mine = ++generation;
    try {
      const next = parseBranding(await get("/branding"));
      if (stopped || mine !== generation || !next) return;
      revision = next.revision;
      onBranding(next);
    } catch {
      // Keep whatever is showing.
    }
  };
  const check = async () => {
    try {
      const { branding: latest } = await get("/version");
      if (!stopped && latest && latest !== revision) await load();
    } catch {
      // API unreachable — nothing to compare against.
    }
  };
  return {
    load,
    check,
    stop: () => {
      stopped = true;
    },
  };
};

const BrandingContext = createContext(DEFAULT_BRANDING);

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(
    () => readCache() ?? DEFAULT_BRANDING,
  );
  const initialRevision = useRef(branding.revision).current;

  useEffect(() => {
    applyDocumentBranding(branding);
  }, [branding]);

  useEffect(() => {
    const sync = createBrandingSync({
      initialRevision,
      onBranding: (next) => {
        writeCache(next);
        setBranding(next);
      },
    });
    const onVisible = () => {
      if (document.visibilityState === "visible") sync.check();
    };

    sync.load();
    const timer = setInterval(sync.check, POLL_MS);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      sync.stop();
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [initialRevision]);

  return (
    <BrandingContext.Provider value={branding}>
      {children}
    </BrandingContext.Provider>
  );
}

export const useBranding = () => useContext(BrandingContext);
